import { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger } from '@/hooks/useGsapAnimations';

gsap.registerPlugin(ScrollTrigger);

const StudySmarter = () => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

    const features = [
        {
            title: "Turn Any Page into Smart Notes",
            subtitle:
                "Scan book pages, PDFs, or handwritten notes and instantly convert them into clean, organized notes you can save and revise anytime.",
            leftImage: "/NotesIcon.png",
            processingImage: "/Processing.svg",
            rightImage: "/NotesMobileImg.png",
            label: "NOTES",
        },
        {
            title: "Get Instant Answers to Questions",
            subtitle:
                "Take a photo of any question or problem and get step-by-step solution to understand and solve it quickly.",
            leftImage: "/QuestionsIcon.png",
            processingImage: "/Processing.svg",
            rightImage: "/QuestionsMobileImg.png",
            label: "QUESTIONS",
        },
        {
            title: "Practice with Auto-Generated Quizzes",
            subtitle:
                "Create quizzes from your study material and test yourself anytime to improve memory and exam confidence.",
            leftImage: "/QuizIcon.png",
            processingImage: "/Processing.svg",
            rightImage: "/QuizMobileImg.png",
            label: "QUIZ",
        },
    ];

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Header animation
            if (headerRef.current) {
                gsap.fromTo(
                    headerRef.current,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.8,
                        ease: 'power2.out',
                        scrollTrigger: {
                            trigger: headerRef.current,
                            start: 'top 85%',
                            toggleActions: 'play none none none',
                        },
                    }
                );
            }

            // Card timeline animations
            cardsRef.current.forEach((card) => {
                if (!card) return;

                const titleEl = card.querySelector('.card-title');
                const leftIcon = card.querySelector('.card-left');
                const processingImg = card.querySelector('.card-processing-img') as HTMLElement;
                const rightMobile = card.querySelector('.card-right');

                const tl = gsap.timeline({
                    scrollTrigger: {
                        trigger: card,
                        start: 'top 85%',
                        toggleActions: 'play none none none',
                    },
                });

                // Card fades in
                tl.fromTo(card, { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' });

                // Title slides in from left
                if (titleEl) {
                    tl.fromTo(titleEl, { opacity: 0, x: -30 }, { opacity: 1, x: 0, duration: 0.5, ease: 'power2.out' }, '-=0.3');
                }

                // Left icon slides in
                if (leftIcon) {
                    tl.fromTo(leftIcon, { opacity: 0, x: -50 }, { opacity: 1, x: 0, duration: 0.6, ease: 'power2.out' }, '-=0.3');
                }

                // Processing image brightens — remove dull class and add pulse
                if (processingImg) {
                    tl.to(processingImg, {
                        filter: 'brightness(1) saturate(1)',
                        duration: 0.8,
                        ease: 'power2.out',
                        onComplete: () => {
                            processingImg.classList.remove('processing-dull');
                            processingImg.classList.add('processing-active');
                        },
                    }, '-=0.2');
                }

                // Right mobile slides in
                if (rightMobile) {
                    tl.fromTo(rightMobile, { opacity: 0, x: 50 }, { opacity: 1, x: 0, duration: 0.6, ease: 'power2.out' }, '-=0.3');
                }
            });
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section ref={sectionRef} className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 py-20  md:py-28 ">
            {/* Header */}
            <div ref={headerRef} className="text-center mb-16 space-y-2" style={{ opacity: 0 }}>
                <h2 className="text-white text-[clamp(24px,4vw,40px)] font-medium">
                    Study smarter, not harder
                </h2>
                <p className="text-[#DCCBFD] max-w-3xl mx-auto text-[clamp(12px,2.5vw,16px)]">
                    From organized subject folders to AI-powered problem solving, we provide
                    the tools you need to master your curriculum with confidence.
                </p>
            </div>

            {/* Cards */}
            <div className="md:space-y-16 space-y-8">
                {features.map((feature, index) => (
                    <div
                        key={index}
                        ref={(el) => { cardsRef.current[index] = el; }}
                        className="relative rounded-[20px] border border-[#FFFFFF1A] bg-[#1a1425]/40 backdrop-blur-sm pt-6 sm:pt-8 px-12 pb-0"
                        style={{ boxShadow: "0 10px 40px rgba(0,0,0,0.4)", opacity: 0 }}
                    >
                        {/* Title */}
                        <div className="card-title mb-4 md:mb-10 max-w-xl ">
                            <h3 className="text-white md:text-[22px] text-[12px] font-semibold mb-3">
                                {feature.title}
                            </h3>
                            <p className="text-[#DCCBFD] md:text-[15px] text-[10px] leading-relaxed">
                                {feature.subtitle}
                            </p>
                        </div>

                        {/* VISUAL FLOW */}
                        <div className="flex justify-center items-center ">
                            {/* LEFT ICONS */}
                            <div className="card-left w-[280px] sm:w-[240px] md:w-[280px]">
                                <img
                                    src={feature.leftImage}
                                    alt={feature.label}
                                    className="w-[200px] sm:w-[240px] md:w-[280px]"
                                />
                            </div>

                            {/* PROCESSING */}
                            <div className="w-[420px] sm:w-[500px]">
                                <img
                                    src={feature.processingImage}
                                    alt="Processing"
                                    className="card-processing-img processing-dull"
                                />
                            </div>

                            {/* RIGHT MOBILE */}
                            <div className="card-right w-[280px] sm:w-[200px] md:w-[220px] lg:w-[260px]">
                                <img
                                    src={feature.rightImage}
                                    alt="Mobile Preview"
                                    className=" "
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

export default StudySmarter;
